import {
  faExclamationCircle,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Column } from 'react-table';
import AppUserFilterDrawer from '../components/AppUsers/AppUserFilterDrawer';

import EmptyContent from '../components/EmptyContent';
import ErrorPage from '../components/ErrorPage';
import PageHeader from '../components/PageHeader';
import Table from '../components/Table';
import TableSkeleton from '../components/TableSkeleton';
import { useAppUsers } from '../hooks/useAppUsers';
import { useParamsToObj } from '../hooks/useParamsToObj';
import { EndUser } from '../types/end-user';

const AppUsers: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = useParamsToObj([
    'search',
    'location',
    'status',
    'nbSharedExperiences_gte',
    'nbSharedExperiences_lte',
  ]);

  const { data, isError, isLoading } = useAppUsers(urlParams);

  const [filterDrawerStatus, setFilterDrawerStatus] = useState(false);

  const columns = React.useMemo<Column<EndUser>[]>(
    () => [
      {
        Header: 'Name',
        accessor: ({ user }) => user?.displayName,
      },
      {
        Header: 'Email',
        accessor: ({ user }) => user?.email,
      },
      {
        Header: 'Area',
        accessor: ({ location }) => location?.city,
      },
      {
        Header: 'Experiences Shared',
        accessor: 'nbSharedExperiences',
      },
      {
        Header: 'Buzzjar Points',
        accessor: 'nbPoints',
      },
      {
        Header: 'Status',
        accessor: ({ isSuspended }) => (
          <span
            className={clsx({
              tag: true,
              'positive-tag': !isSuspended,
              'alert-tag': isSuspended,
            })}
          >
            {isSuspended ? 'Inactive' : 'Active'}
          </span>
        ),
      },
      {
        Header: ' ',
        disableSortBy: true,
        accessor: buzzlUser => (
          <button
            onClick={() => {
              navigate(`/app-users/${buzzlUser.id}`);
            }}
            className="text-link"
          >
            View
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (isLoading)
    return (
      <TableSkeleton
        headers={[
          'Name',
          'Email',
          'Area',
          'Experiences Shared',
          'Buzzjar Points',
          'Status',
          ' ',
        ]}
      />
    );

  if (isError || !data) {
    return (
      <ErrorPage
        title="Something went wrong!"
        text="We keep track of these errors, but feel free to contact us if refreshing doesn't fix things."
      />
    );
  }

  if (data.length === 0 && !location.search) {
    return (
      <div className="mt-32 flex h-full items-center justify-center">
        <EmptyContent title="No App Users" description="F" icon={faUsers} />
      </div>
    );
  }

  return (
    <div>
      <AppUserFilterDrawer
        open={filterDrawerStatus}
        onClose={() => setFilterDrawerStatus(false)}
      />
      <PageHeader
        urlParams={urlParams}
        title="Mobile App Users"
        filterBtnAction={() => setFilterDrawerStatus(true)}
        searchPlaceholder="Search Username"
      />
      {data.length > 0 ? (
        <Table columns={columns} data={data} />
      ) : (
        <div className="mt-32 flex h-full items-center justify-center">
          <EmptyContent
            title={`No results found`}
            description="Please change the search terms to match results."
            icon={faExclamationCircle}
            btnAction={() => {
              navigate({
                search: ``,
              });
            }}
            btnText="Reset filters"
          />
        </div>
      )}
    </div>
  );
};

export default AppUsers;
